/**
 * Changing how you look, at somebody who does that for a living.
 *
 * Opened by a character rather than from a menu: the world takes whatever it
 * costs, opens this, and only then will it listen to a change of appearance.
 * Closing it without keeping anything leaves the fee spent, which is what a
 * barber is.
 *
 * The same two colours the character screen asks about, so somebody who made a
 * character already knows this window.
 */

import { useState } from 'react';
import { CharacterPreview } from './CharacterPreview';
import { CloseButton } from './CloseButton';
import { ColourChoice } from './Colours';
import { colourValue } from './colour-values';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';

/** A number as the world holds it, said as a colour a picker understands. */
function said(value: number): string {
  return `#${(value >>> 0).toString(16).padStart(6, '0')}`;
}

export function Appearance({
  skin,
  eyes,
  onKeep,
  onClose,
}: {
  /** What they look like now, as the world holds it. */
  skin: number;
  eyes: number;
  onKeep: (skin: number, eyes: number) => void;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('appearance');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('appearance', nudge, altHeld, {
    width: 300,
    height: 240,
  });

  // Held here until it is kept, so a colour tried and thought better of costs
  // nothing and changes nothing.
  const [held, setHeld] = useState({ skin: said(skin), eyes: said(eyes) });

  return (
    <div
      className={`trade appearance${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Appearance</span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="panel-body appearance-body">
        <CharacterPreview
          skin={colourValue(held.skin)}
          eyes={colourValue(held.eyes)}
          width={160}
          height={210}
        />

        <ColourChoice
          skin={held.skin}
          eyes={held.eyes}
          onChange={(which, colour) => setHeld((was) => ({ ...was, [which]: colour }))}
        />

        <button
          type="button"
          className="wide primary"
          onClick={() => onKeep(colourValue(held.skin), colourValue(held.eyes))}
        >
          Keep This Look
        </button>
      </div>
      {grips}
    </div>
  );
}
